import React from 'react';
import { Competence, Service } from '../../../../../../shared/types/config';

interface CompetenceModalProps {
  editingCompetence: Competence | null;
  competenceForm: Omit<Competence, 'id'>;
  setCompetenceForm: React.Dispatch<React.SetStateAction<Omit<Competence, 'id'>>>;
  services: Service[];
  onClose: () => void;
  onSave: () => Promise<void>;
}

const CompetenceModal: React.FC<CompetenceModalProps> = ({
  editingCompetence,
  competenceForm,
  setCompetenceForm,
  services,
  onClose,
  onSave,
}) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative z-50 bg-gray-900 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-gray-700">
        <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gray-800/50">
          <h2 className="text-2xl font-bold text-white">
            {editingCompetence ? 'Modifier la compétence' : 'Ajouter une compétence'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nom *</label>
              <input
                type="text"
                value={competenceForm.nom}
                onChange={(e) => setCompetenceForm({ ...competenceForm, nom: e.target.value })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Type *</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-gray-300">
                  <input
                    type="radio"
                    name="type"
                    checked={!competenceForm.is_specialite}
                    onChange={() => setCompetenceForm({ ...competenceForm, is_specialite: false })}
                    className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-700 focus:ring-blue-500"
                  />
                  Compétence normale
                </label>
                <label className="flex items-center gap-2 text-gray-300">
                  <input
                    type="radio"
                    name="type"
                    checked={competenceForm.is_specialite}
                    onChange={() => setCompetenceForm({ ...competenceForm, is_specialite: true })}
                    className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-700 focus:ring-blue-500"
                  />
                  Spécialité
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Service</label>
              <select
                value={competenceForm.id_service || ''}
                onChange={(e) => setCompetenceForm({ ...competenceForm, id_service: e.target.value ? parseInt(e.target.value) : null })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Générale (tous services)</option>
                {services.map(service => (
                  <option key={service.id} value={service.id}>{service.nom}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">Laissez vide pour une compétence générale disponible pour tous les services</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
              <textarea
                value={competenceForm.description || ''}
                onChange={(e) => setCompetenceForm({ ...competenceForm, description: e.target.value })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Description de la compétence..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Ordre d'affichage (optionnel)
                <span className="ml-2 text-xs text-gray-400">(définit la hiérarchie/fonction, ex: 1 = CA SAP, 2 = COND VSAV, etc.)</span>
              </label>
              <input
                type="number"
                value={competenceForm.ordre ?? ''}
                onChange={(e) => setCompetenceForm({ ...competenceForm, ordre: e.target.value ? parseInt(e.target.value) : undefined })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="1"
                placeholder="Ex: 1, 2, 3..."
              />
              <p className="mt-1 text-xs text-gray-400">Les compétences seront triées par ordre croissant. Si non défini, elles seront triées par nom.</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-4 p-6 border-t border-gray-700 bg-gray-800/50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
          >
            {editingCompetence ? 'Modifier' : 'Ajouter'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompetenceModal;

